import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';

// Page imports
import { Dashboard } from '@/pages/Dashboard';
import { ClientList } from '@/pages/clients/ClientList';
import { ClientDetail } from '@/pages/clients/ClientDetail';
import { ProspectList, ProspectDetail, OpportunityList, OpportunityDetail, CompetitorList, CaseList, CaseDetail } from '@/pages/sales';
import { Products } from '@/pages/Products';
// Financials module — hidden for production launch.
// 
// The 11 pages under client/src/pages/financials are driven entirely by
// hardcoded literals: there are no tables, no GraphQL schema and no
// resolvers behind any of them. Shipping them would have presented invented
// ledgers, AR aging and cash balances as real financials.
// 
// The pages are left in place, untouched. Restoring the module means
// uncommenting this block (and the matching one in Sidebar.tsx) once it is
// wired to an API.
// import { FinancialsDashboard, ChartOfAccounts, VendorList, VendorDetail, GeneralLedger, BillList, PaymentList, BankAccounts, BankAccountDetail, BankImport, FinancialReports } from '@/pages/financials';
import { Communications } from '@/pages/Communications';
import { NotFound } from '@/pages/NotFound';

// Placeholder components for pages we'll build later
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-[60vh] text-center">
      <h1 className="text-2xl font-semibold text-slate dark:text-mist mb-2">
        {title}
      </h1>
      <p className="text-chalkboard dark:text-rain-cloud">
        This page is coming soon.
      </p>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected routes - Main app shell with sidebar */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route path="/" element={<Dashboard />} />

        {/* Sales */}
        <Route path="/sales" element={<Navigate to="/sales/prospects" replace />} />
        <Route path="/sales/prospects" element={<ProspectList />} />
        <Route path="/sales/prospects/:prospectId" element={<ProspectDetail />} />
        <Route path="/sales/opportunities" element={<OpportunityList />} />
        <Route path="/sales/opportunities/:opportunityId" element={<OpportunityDetail />} />
        <Route path="/sales/competitors" element={<CompetitorList />} />

        {/* Cases (Service Desk) */}
        <Route path="/cases" element={<CaseList />} />
        <Route path="/cases/:caseId" element={<CaseDetail />} />

        {/* Clients */}
        <Route path="/clients" element={<ClientList />} />
        <Route path="/clients/:clientId" element={<ClientDetail />} />

        {/* Products */}
        <Route path="/products" element={<Products />} />

        {/* Financials — hidden for launch; see the note by the import above.
        <Route path="/financials" element={<FinancialsDashboard />} />
        <Route path="/financials/accounts" element={<ChartOfAccounts />} />
        <Route path="/financials/vendors" element={<VendorList />} />
        <Route path="/financials/vendors/:vendorId" element={<VendorDetail />} />
        <Route path="/financials/ledger" element={<GeneralLedger />} />
        <Route path="/financials/bills" element={<BillList />} />
        <Route path="/financials/payments" element={<PaymentList />} />
        <Route path="/financials/banking" element={<BankAccounts />} />
        <Route path="/financials/banking/:accountId" element={<BankAccountDetail />} />
        <Route path="/financials/banking/import" element={<BankImport />} />
        <Route path="/financials/reports" element={<FinancialReports />} />
        */}

        {/* Communications (legacy) */}
        <Route path="/communications" element={<Communications />} />

        {/* Settings */}
        <Route path="/settings" element={<PlaceholderPage title="Settings" />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
